/**
 * GoobDoob VTT — character data model.
 *
 * Implements the dual-sheet mechanic: every character has two ledger sheets
 * (Sheet A = relearning, Sheet B = original timeline) that always level
 * together. Players see one merged sheet during play. The split is only
 * visible on the level-up page and the management buttons on the play view.
 *
 * Both sheets share the same rolled `base` stats. Each sheet stores only
 * its deltas (ASIs, feats, magic items). Merged values are derived.
 *
 * The eventual story merge is just: flatten all deltas into base, archive
 * both sheets. No data migration needed — that's why we model it this way
 * from day one.
 */

export type AbilityScore = 'STR' | 'DEX' | 'CON' | 'INT' | 'WIS' | 'CHA';

export type AbilityBlock = Record<AbilityScore, number>;

export type ProficiencyLevel = 'proficiency' | 'expertise';

/**
 * A single spell on a character's known/prepared list. Per-sheet — Sheet A's
 * wizard spells and Sheet B's paladin spells are tracked independently.
 */
export interface SpellEntry {
  /** Spell name as it appears in the PHB / D&D Beyond. */
  name: string;
  /** 0 = cantrip, 1–9 = leveled spell. Determines minimum slot level to cast. */
  level: number;
  /** Arcane school (evocation, abjuration, etc.). */
  school?: string;
  /** Casting time — "1 action", "1 bonus action", "1 reaction", etc. */
  castingTime?: string;
  /** Range — "Self", "Touch", "120 ft.", "30/60 ft. cone", etc. */
  range?: string;
  /** Component letters + material note, e.g. "V, S, M (a bit of fleece)". */
  components?: string;
  /** Duration — "Instantaneous", "Concentration, up to 1 minute", etc. */
  duration?: string;
  /** Dice expression for damage/healing, e.g. "2d10" or "1d8+3". */
  damage?: string;
  /** Damage type if applicable, e.g. "fire", "necrotic", "radiant". */
  damageType?: string;
  /** Whether the spell requires a spell attack roll or forces a save. */
  attackType?: 'melee' | 'ranged' | 'save';
  /** For save-based spells, the save ability the target rolls. */
  saveAbility?: AbilityScore;
  /** Free-form notes (higher-level cast effects, etc.). */
  notes?: string;
  /** For prepared casters — whether this spell is currently prepared. */
  prepared?: boolean;
  /** Ritual tag. */
  ritual?: boolean;
  /** Source book or feature that grants this spell (e.g. "Magic Initiate"). */
  source?: string;
}

/** Per-sheet ledger. Only deltas. The base stats live on the parent character. */
export interface SheetLedger {
  /** Class taken on this sheet (e.g. 'Paladin', 'Warlock'). */
  className: string;
  /** Subclass / archetype (e.g. 'Oath of the Watchers'). */
  subclass?: string;
  /** Level on this sheet. ALWAYS equal to the other sheet's level. */
  level: number;
  /** Total of all hit die rolls/averages on this sheet. CON mod is added separately. */
  hitDieTotal: number;
  /** Stat increases from ASI choices on this sheet. */
  asiDeltas: Partial<AbilityBlock>;
  /** Stat increases from feats taken on this sheet. */
  featDeltas: Partial<AbilityBlock>;
  /** Stat increases from magic items currently attuned on this sheet. */
  magicItemDeltas: Partial<AbilityBlock>;
  /** Skill / save proficiencies. Expertise wins on merge. */
  proficiencies: Record<string, ProficiencyLevel>;
  /** Feats taken on this sheet, as feature-id strings. */
  feats: string[];
  /**
   * Known/prepared spell list. Populated by import or manual entry on the
   * Level Up page. Cantrips (level 0) and leveled spells coexist in one list.
   */
  spells?: SpellEntry[];
}

export interface Character {
  id: string;
  name: string;
  /** Shared rolled base stats — identical for both sheets. */
  base: AbilityBlock;
  sheetA: SheetLedger;
  sheetB: SheetLedger;
  /** Which sheet's CON modifier counts toward HP. Tunable per character. */
  conSheet: 'A' | 'B';

  // ── Character-level metadata (shared across both sheets) ──

  /** Race or species, e.g. "Half-Elf", "Dragonborn". */
  race?: string;
  /** Background, e.g. "Sage", "Criminal". */
  background?: string;
  /** Alignment, e.g. "Chaotic Good". */
  alignment?: string;
  /** Base walking speed in feet. */
  walkSpeed?: number;
  /** Known languages. */
  languages?: string[];
  /** Personality traits from the background. */
  personalityTraits?: string;
  /** Character ideals. */
  ideals?: string;
  /** Character bonds. */
  bonds?: string;
  /** Character flaws. */
  flaws?: string;
}

// ---------- derived values ----------

export const ZERO_DELTAS: Partial<AbilityBlock> = {};

const sumDelta = (
  stat: AbilityScore,
  ...deltas: Partial<AbilityBlock>[]
): number => deltas.reduce((acc, d) => acc + (d[stat] ?? 0), 0);

/** Merged ability score = base + every delta from both sheets. */
export function mergedScore(c: Character, stat: AbilityScore): number {
  return (
    c.base[stat] +
    sumDelta(stat, c.sheetA.asiDeltas, c.sheetA.featDeltas, c.sheetA.magicItemDeltas) +
    sumDelta(stat, c.sheetB.asiDeltas, c.sheetB.featDeltas, c.sheetB.magicItemDeltas)
  );
}

/** A single sheet's view of a stat = base + that sheet's deltas only. */
export function sheetScore(
  c: Character,
  sheet: 'A' | 'B',
  stat: AbilityScore,
): number {
  const ledger = sheet === 'A' ? c.sheetA : c.sheetB;
  return (
    c.base[stat] +
    sumDelta(stat, ledger.asiDeltas, ledger.featDeltas, ledger.magicItemDeltas)
  );
}

export function mergedScores(c: Character): AbilityBlock {
  return {
    STR: mergedScore(c, 'STR'),
    DEX: mergedScore(c, 'DEX'),
    CON: mergedScore(c, 'CON'),
    INT: mergedScore(c, 'INT'),
    WIS: mergedScore(c, 'WIS'),
    CHA: mergedScore(c, 'CHA'),
  };
}

export function abilityModifier(score: number): number {
  return Math.floor((score - 10) / 2);
}

/**
 * Play level per sheet: min of both sheets' levels.
 * Both sheets level together during actual play; a sheet pre-levelled
 * beyond that (e.g. for path planning) doesn't count as earned yet.
 */
export function playLevel(c: Character): number {
  return Math.min(c.sheetA.level, c.sheetB.level);
}

/** Combined level = sum of both sheets' levels (capped at play level each). */
export function combinedLevel(c: Character): number {
  return playLevel(c) * 2;
}

/** PB scales off the combined level: ceil(1 + L/4). Standard 5e curve, just doubled. */
export function proficiencyBonus(c: Character): number {
  return Math.ceil(1 + combinedLevel(c) / 4);
}

/**
 * Total HP: one sheet contributes the CON modifier per its play level, the
 * other sheet contributes only its hit die total. Whichever sheet is the
 * "CON sheet" is set per-character via `conSheet`.
 *
 * Uses playLevel() (min of both sheets) for the CON multiplier so that a
 * sheet pre-levelled beyond the actual play level doesn't inflate HP.
 */
export function totalHp(c: Character): number {
  const conMod = abilityModifier(mergedScore(c, 'CON'));
  const conLedger = c.conSheet === 'A' ? c.sheetA : c.sheetB;
  const otherLedger = c.conSheet === 'A' ? c.sheetB : c.sheetA;
  const lvl = playLevel(c);
  return conLedger.hitDieTotal + lvl * conMod + otherLedger.hitDieTotal;
}

/** Merged proficiencies: union of both sheets. Expertise wins over proficiency. */
export function mergedProficiencies(
  c: Character,
): Record<string, ProficiencyLevel> {
  const out: Record<string, ProficiencyLevel> = { ...c.sheetA.proficiencies };
  for (const [skill, level] of Object.entries(c.sheetB.proficiencies)) {
    if (out[skill] === 'expertise' || level === 'expertise') {
      out[skill] = 'expertise';
    } else {
      out[skill] = 'proficiency';
    }
  }
  return out;
}

/**
 * Feat / ASI prerequisite validation.
 *
 *   Sheet A feats validate against Sheet A scores ONLY.
 *   Sheet B feats validate against MERGED scores.
 *
 * The asymmetry reflects the lore: Sheet A is "relearning" the second
 * timeline independently, Sheet B "remembers" the original timeline state
 * where both halves were already fully expressed.
 */
export type Prerequisite = Partial<Record<AbilityScore, number>>;

export function meetsPrerequisite(
  c: Character,
  sheet: 'A' | 'B',
  prereq: Prerequisite,
): boolean {
  const lookup = (stat: AbilityScore) =>
    sheet === 'A' ? sheetScore(c, 'A', stat) : mergedScore(c, stat);
  for (const [stat, required] of Object.entries(prereq) as [
    AbilityScore,
    number,
  ][]) {
    if (lookup(stat) < required) return false;
  }
  return true;
}
